<template>
  <div class="payment-element mt-3" data-cy="payment-element">
    <div class="payment-element__wrapper">
      <div ref="paymentElement" id="payment-element"></div>
    </div>
    <div
      v-if="error"
      data-cy="payment-element-errors"
      class="payment-element__errors"
      role="alert"
    >
      <v-icon icon="error" />
      <span>{{ error }}</span>
    </div>

    <div class="payment-element__components">
      <transition name="fade" mode="out-in">
        <template v-if="isInitialized">
          <div class="payment-element__cta-row d-flex align-items-center my-2">
            <v-button
              data-cy="payment-element-back"
              class="mr-1"
              secondary
              round
              @click="handleBack"
            >
              <v-icon icon="arrow_back" />
            </v-button>

            <v-button
              data-cy="payment-element-submit"
              primary
              block
              :disabled="!isValid"
              :is-loading="isLoading"
              @click="handleSubmit"
            >
              {{ buttonText }}
            </v-button>
          </div>
        </template>

        <template v-else>
          <div class="payment-element__placeholder">
            <div class="d-flex w-100">
              <div class="payment-element__placeholder-element"></div>
              <div class="payment-element__placeholder-element"></div>
            </div>
            <div class="d-flex w-100 my-3">
              <div class="payment-element__placeholder-element"></div>
            </div>
            <div class="d-flex w-100 my-3">
              <div class="payment-element__placeholder-element"></div>
              <div class="payment-element__placeholder-element"></div>
            </div>
          </div>
        </template>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex"

import { isPresent } from "@/lib/utils"
import useStripe from "@/lib/mixins/use-stripe"
import { generateReturnURL, INTENT_TYPES } from "@/lib/checkout-utils"

import { ACTIVITY_TYPES } from "@/store/analytics"

export default {
  mixins: [useStripe("setupForm")],

  props: {
    intentType: { type: String, default: null }, // null triggers loading state
    intentClientSecret: { type: String, default: null }, // null triggers loading state
    productId: { type: [String, Number], default: null },
    amount: { type: [String, Number], default: null },
    currency: { type: String, default: null },
    symbol: { type: String, default: "$" },
    discountInfo: { type: Object, default: () => ({}) },
    hasTrial: { type: Boolean, default: false },
    confirmText: { type: String, default: null }
  },

  data() {
    return {
      error: null,
      isValid: false,
      isInitialized: false,
      isLoading: false,
      elements: null,
      paymentElement: null
    }
  },

  computed: {
    buttonText() {
      return isPresent(this.confirmText)
        ? this.confirmText
        : this.hasTrial
        ? "Start free trial"
        : isPresent(this.discountInfo)
        ? `Pay ${this.discountInfo.symbol}${
            this.discountInfo.final_amount
          } ${this.currency?.toUpperCase()}`
        : isPresent(this.amount) && isPresent(this.currency)
        ? `Pay ${this.symbol}${this.amount} ${this.currency?.toUpperCase()}`
        : "Pay"
    },

    hasIntentInfo() {
      return isPresent(this.intentType) && isPresent(this.intentClientSecret)
    }
  },

  watch: {
    hasIntentInfo() {
      if (this.hasIntentInfo) this.setupForm()
    }
  },

  methods: {
    ...mapActions("analytics", ["trackActivity"]),

    setupForm() {
      if (!this.hasIntentInfo) {
        return
      }

      if (!this.$refs.paymentElement) {
        setTimeout(() => this.setupForm(), 500)
      } else {
        try {
          this.elements = this.useStripe_stripe.elements({
            appearance: { theme: "stripe" },
            clientSecret: this.intentClientSecret
          })

          this.paymentElement = this.elements.create("payment")
          this.paymentElement.mount(this.$refs.paymentElement)

          this.paymentElement.on("change", this.handlePaymentElementChange)

          this.paymentElement.on("loaderstart", () => {
            this.isInitialized = true
          })
        } catch (e) {
          console.error(e)
          this.$rollbar.critical(e)
        }
      }
    },

    handleSubmit(e) {
      e.preventDefault()
      this.hideMessage()

      this.isLoading = true

      const returnUrl = generateReturnURL(window.location, this.productId)

      if (this.intentType === INTENT_TYPES.setup_intent) {
        this.useStripe_stripe
          .confirmSetup({
            elements: this.elements,
            confirmParams: {
              return_url: returnUrl
            }
          })
          .then(({ error }) => this.handleError(error))
      } else {
        this.useStripe_stripe
          .confirmPayment({
            elements: this.elements,
            confirmParams: {
              return_url: returnUrl
            }
          })
          .then(({ error }) => this.handleError(error))
      }
    },

    handleError(error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment
      if (
        error.type === "card_error" ||
        error.type === "validation_error" ||
        error.type === "invalid_request_error"
      ) {
        this.showMessage(error.message)
        this.trackError(error.type, error.message)
      } else {
        // Report to bugsnag
        this.showMessage("An unexpected error occurred.")
        this.trackError("unknown_error", "An unexpected error occurred.")
      }

      this.isLoading = false
    },

    showMessage(message) {
      this.error = message
    },

    hideMessage() {
      this.error = null
    },

    handleBack() {
      this.isInitialized = false
      this.$nextTick(() => {
        this.$emit("back")
      })
    },

    handlePaymentElementChange(event) {
      this.isValid = event.complete
    },

    trackError(reason, message) {
      this.trackActivity({
        activityType: ACTIVITY_TYPES.CHECKOUT_ERROR,
        data: {
          reason,
          message,
          product: { id: this.productId }
        }
      })
    }
  },

  beforeDestroy() {
    this.isInitialized = false
    this.elements?.getElement("payment")?.destroy()
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.payment-element__errors {
  margin: $size--4 0;
  display: flex;
  border: 1px solid $color-ui--red;
  box-shadow: $box-shadow--small;
  padding: $size--4;
  border-radius: 5px;
  color: $color-ui--grey-90;
  font-size: $font-size--base;
  animation: shake-x 0.6s ease;

  .v-icon {
    color: $color-ui--red;
    font-size: $font-size--large;
    margin-right: $size--2;
  }
}

.payment-element__placeholder-element {
  border: 1px solid $color-ui--grey-30;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02);
  border-radius: 5px;
  padding: $size--6;
  flex-grow: 1;
  margin: 0 $size--1;
  animation: load-pulse 1.2s infinite ease-in-out;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    left: $size--3;
    top: $size--3;
    height: $size--5;
    width: 33%;
    background: $color-ui--grey-15;
  }
}

.payment-element__cta-row {
  opacity: 0;
  animation: fade-in 0.4s ease forwards;
  animation-delay: 0.8s;
}
</style>
